import { useMemo } from 'react';
import { type SWRResponse } from 'swr';

import type ApiError from '@/helpers/ApiError';
import { type SwrKey, type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import noodleApiClient, { type ApiDefinition } from '@/tsClient/servers/noodleApi';
import buildPath from '@/tsClient/utilities/buildPath';

const PATH_TEMPLATE = '/custom-statuses';
export type CustomStatusesApi = ApiDefinition[typeof PATH_TEMPLATE]['GET'];

export const useCustomStatuses = (
  query: CustomStatusesApi['query'] | null,
  config?: UseNoodleSwrConfig<CustomStatusesApi['response']>,
): SWRResponse<CustomStatusesApi['response'], ApiError> => {
  const path = query ? buildPath<CustomStatusesApi>(PATH_TEMPLATE) : null;
  // When query exists, pass it as Axios params via the SWR key tuple
  const key = useMemo(() => (query && path ? [path, { params: query }] : null) satisfies SwrKey, [query, path]);
  return useNoodleSwr(key, { revalidateOnFocus: false, ...config });
};

/**
 * @deprecated: Please use `useCustomStatuses` wherever a perfectly fresh response is not worth the network time, bandwidth, and compute.
 */
const getCustomStatuses = async ({
  creatorId,
  includeArchived,
}: CustomStatusesApi['query']): Promise<CustomStatusesApi['response']> => {
  const path = buildPath<CustomStatusesApi>(PATH_TEMPLATE);
  const data = await noodleApiClient.get(path, {
    params: {
      creatorId,
      includeArchived,
    },
  });
  return data.data;
};

export default getCustomStatuses;
